/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import type {
  PrenigmaCollaborationEnvelope,
  PrenigmaCollaborationRuntimeSourceProducer,
} from "./collaborationRuntime";

export interface PrenigmaTtsControllableMediaElement {
  readonly paused: boolean;
  currentTime: number;
  pause: () => void;
}

export interface PrenigmaTtsMediaControlRefs {
  bargeInSourceEventRef: string;
  bargeInTimelineReadbackRef: string;
  obsoleteTtsStreamRef: string;
  obsoleteTurnId: string;
  supersedesTurnId: string;
  ttsCancelAckRef: string;
}

export interface CancelPrenigmaTtsMediaPlaybackOptions {
  correlationIds?: string[];
  media: PrenigmaTtsControllableMediaElement;
  producer: PrenigmaCollaborationRuntimeSourceProducer;
  refs: PrenigmaTtsMediaControlRefs;
}

export interface CancelPrenigmaTtsMediaPlaybackResult {
  bargeIn: PrenigmaCollaborationEnvelope;
  cancelVerified: boolean;
  currentTimeResetAfterCancel: boolean;
  obsoleteAudioSuppressed: boolean;
  pausedAfterCancel: boolean;
  ttsCancel: PrenigmaCollaborationEnvelope;
  ttsCancelAcknowledged: boolean;
}

export function cancelPrenigmaTtsMediaPlayback({
  correlationIds = [],
  media,
  producer,
  refs,
}: CancelPrenigmaTtsMediaPlaybackOptions):
  CancelPrenigmaTtsMediaPlaybackResult {
  requireTtsMediaControlRefs(refs);

  media.pause();
  try {
    media.currentTime = 0;
  } catch {
    throw new Error("prenigma_tts_media_current_time_reset_failed");
  }

  const pausedAfterCancel = media.paused === true;
  const currentTimeResetAfterCancel = media.currentTime < 0.25;
  const cancelVerified = pausedAfterCancel && currentTimeResetAfterCancel;
  if (!cancelVerified) {
    throw new Error("prenigma_tts_media_cancel_not_verified");
  }

  const bargeIn = producer.emitBargeIn({
    bargeInId: `barge_in_${refs.obsoleteTurnId}`,
    obsoleteTurnId: refs.obsoleteTurnId,
    supersedesTurnId: refs.supersedesTurnId,
    obsoleteTtsStreamRef: refs.obsoleteTtsStreamRef,
    bargeInSourceEventRef: refs.bargeInSourceEventRef,
    bargeInTimelineReadbackRef: refs.bargeInTimelineReadbackRef,
    sourceEventReadbackVerified: true,
    timelineReadbackVerified: true,
    correlationIds,
  });
  const ttsCancel = producer.emitTtsCancel({
    cancelId: `tts_cancel_${refs.obsoleteTurnId}`,
    obsoleteTurnId: refs.obsoleteTurnId,
    obsoleteTtsStreamRef: refs.obsoleteTtsStreamRef,
    ttsCancelAckRef: refs.ttsCancelAckRef,
    bargeInSourceEventRef: refs.bargeInSourceEventRef,
    bargeInTimelineReadbackRef: refs.bargeInTimelineReadbackRef,
    ttsCancelAcknowledged: true,
    obsoleteAudioSuppressed: true,
    sourceEventReadbackVerified: true,
    timelineReadbackVerified: true,
    causationEventIds: [bargeIn.event_id],
    correlationIds,
  });

  return {
    bargeIn,
    cancelVerified,
    currentTimeResetAfterCancel,
    obsoleteAudioSuppressed: true,
    pausedAfterCancel,
    ttsCancel,
    ttsCancelAcknowledged: true,
  };
}

function requireTtsMediaControlRefs(refs: PrenigmaTtsMediaControlRefs): void {
  requireText(refs.obsoleteTurnId, "refs.obsoleteTurnId");
  requireText(refs.supersedesTurnId, "refs.supersedesTurnId");
  requireText(refs.obsoleteTtsStreamRef, "refs.obsoleteTtsStreamRef");
  requireText(refs.bargeInSourceEventRef, "refs.bargeInSourceEventRef");
  requireText(
    refs.bargeInTimelineReadbackRef,
    "refs.bargeInTimelineReadbackRef",
  );
  requireText(refs.ttsCancelAckRef, "refs.ttsCancelAckRef");
}

function requireText(value: string, path: string): void {
  if (!value.trim()) {
    throw new Error(`${path} is required`);
  }
}
